import { useCallback, useEffect, useRef, useState } from "react";
import {
  Wallet, Loader2, CheckCircle2, Trash2, Plus, HandCoins, BadgeDollarSign,
  Undo2, FileText, Mail, Edit2, X,
} from "lucide-react";
import { PageHeader, Empty } from "@/components/UI";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { formatCurrency } from "@/lib/format";

const fmt = (v, cur = "UGX") => formatCurrency(v || 0, cur);

// Uganda statutory: NSSF employee 5% + monthly PAYE bands
const statutoryCalc = (gross) => {
  const g = parseFloat(gross) || 0;
  const nssf = g * 0.05;
  let paye = 0;
  if (g > 410000) paye = 25000 + (g - 410000) * 0.3;
  else if (g > 335000) paye = 10000 + (g - 335000) * 0.2;
  else if (g > 235000) paye = (g - 235000) * 0.1;
  return { nssf, paye };
};

export default function HRPayroll() {
  const { user } = useAuth();
  const { toast } = useToast();
  const isAdmin = user?.role === "admin";
  const allowed = isAdmin || user?.hr_access;
  const [data, setData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [monthFilter, setMonthFilter] = useState("");
  const [modal, setModal] = useState(null); // {employee, type}
  const [busy, setBusy] = useState(null);
  const [empModal, setEmpModal] = useState(null);
  const [empSaving, setEmpSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [ov, pays] = await Promise.all([
        api.hrOverview(),
        api.hrPayments(monthFilter ? { month: monthFilter } : {}),
      ]);
      setData(ov);
      setPayments(pays);
    } catch (e) {
      toast(e?.response?.data?.detail || "Failed to load HR data", "error");
    }
  }, [monthFilter, toast]);

  useEffect(() => { if (allowed) load(); }, [allowed, load]);

  if (!allowed) {
    return <MyPayslips />;
  }
  if (!data) return <div className="p-8 text-center"><Loader2 className="animate-spin inline" /></div>;

  const s = data.summary;

  const approve = async (p) => {
    const emp = data.employees.find((e) => e.id === p.user_id);
    const out = emp?.outstanding_advance || 0;
    let deduct = 0;
    if (out > 0) {
      const ans = window.prompt(
        `${p.employee_name} has an outstanding advance of ${fmt(out)}.\nAmount to deduct from this salary (0 for none):`, `${Math.min(out, p.amount)}`);
      if (ans === null) return;
      deduct = parseFloat(ans) || 0;
    }
    setBusy(p.id);
    try {
      await api.hrApprovePayment(p.id, { deduct_advance: deduct });
      toast(`Salary approved & posted to expenses — ${p.employee_name}`, "success");
      load();
    } catch (e) { toast(e?.response?.data?.detail || "Approve failed", "error"); }
    setBusy(null);
  };

  const removePayment = async (p) => {
    if (!window.confirm(`Delete this ${p.type} record for ${p.employee_name}? The linked expense entry is removed too.`)) return;
    try { await api.hrDeletePayment(p.id); toast("Payment deleted", "success"); load(); }
    catch (e) { toast(e?.response?.data?.detail || "Delete failed", "error"); }
  };

  const recover = async (p) => {
    const out = (p.amount || 0) - (p.recovered || 0);
    const ans = window.prompt(`Mark how much of this ${fmt(p.amount)} advance as recovered? Outstanding: ${fmt(out)}`, `${out}`);
    if (ans === null) return;
    const amt = parseFloat(ans) || 0;
    if (amt <= 0) return;
    try { await api.hrRecoverAdvance(p.id, amt); toast("Advance recovery recorded", "success"); load(); }
    catch (e) { toast(e?.response?.data?.detail || "Failed", "error"); }
  };

  const saveSalary = async (emp, val) => {
    const salary = parseFloat(val) || 0;
    if (salary === (emp.monthly_salary || 0)) return;
    try { await api.hrUpdateProfile(emp.id, { monthly_salary: salary }); toast(`Salary updated for ${emp.full_name || emp.email}`, "success"); load(); }
    catch { toast("Failed to update salary", "error"); }
  };

  const toggleHr = async (emp) => {
    try { await api.hrSetAccess(emp.id, !emp.hr_access); toast(`HR access ${emp.hr_access ? "revoked" : "granted"}`, "success"); load(); }
    catch (e) { toast(e?.response?.data?.detail || "Failed", "error"); }
  };

  const saveEmployee = async () => {
    if (!empModal.full_name.trim()) { toast("Employee name is required", "error"); return; }
    setEmpSaving(true);
    const body = {
      full_name: empModal.full_name, position: empModal.position, department: empModal.department,
      email: empModal.email, phone: empModal.phone, nin: empModal.nin, address: empModal.address,
      dob: empModal.dob, start_date: empModal.start_date, emergency_contact: empModal.emergency_contact,
      notes: empModal.notes, payment_method: empModal.payment_method,
      account_details: empModal.account_details, currency: empModal.currency || "UGX",
      monthly_salary: parseFloat(empModal.monthly_salary) || 0,
    };
    try {
      if (empModal.id) {
        if (!empModal.standalone) delete body.full_name;
        await api.hrUpdateProfile(empModal.id, body);
        toast("Employee file updated", "success");
      } else {
        await api.hrCreateEmployee(body);
        toast(`${body.full_name} added to HR`, "success");
      }
      setEmpModal(null); load();
    } catch (e) { toast(e?.response?.data?.detail || "Save failed", "error"); }
    setEmpSaving(false);
  };

  const removeEmployee = async (e) => {
    if (!window.confirm(`Delete ${e.full_name}'s employee file? Payment history is kept.`)) return;
    try { await api.hrDeleteEmployee(e.id); toast("Employee file deleted", "success"); load(); }
    catch (err) { toast(err?.response?.data?.detail || "Failed", "error"); }
  };

  const toggleStatutory = async (emp) => {
    try {
      await api.hrUpdateProfile(emp.id, { statutory: !emp.statutory });
      toast(`NSSF & PAYE ${emp.statutory ? "disabled" : "enabled"} for ${emp.full_name || emp.email}`, "success");
      load();
    } catch (e) { toast(e?.response?.data?.detail || "Failed", "error"); }
  };

  const downloadPayslip = async (p) => {
    setBusy(`slip-${p.id}`);
    try {
      const r = await api.hrPayslip(p.id);
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `PAYSLIP_${(p.employee_name || "employee").replace(/\s+/g, "_")}_${p.month}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast("Failed to generate payslip", "error"); }
    setBusy(null);
  };

  const emailPayslip = async (p) => {
    if (!window.confirm(`Email the ${p.month} payslip to ${p.employee_name}'s email on file?`)) return;
    setBusy(`mail-${p.id}`);
    try {
      const r = await api.hrEmailPayslip(p.id);
      toast(`Payslip emailed to ${r.to}`, "success");
      load();
    } catch (e) { toast(e?.response?.data?.detail || "Failed to email payslip", "error"); }
    setBusy(null);
  };

  return (
    <div data-testid="page-hr">
      <PageHeader
        icon={Wallet}
        title="HR & Payroll"
        subtitle="Salaries, advances & reimbursements — every payment auto-posts to Books of Accounts"
        action={
          <div className="flex gap-2">
            <button className="btn btn-secondary" data-testid="hr-add-employee" onClick={() => setEmpModal({ ...EMPTY_EMP })}>
              <Plus size={14} /> Add Employee
            </button>
            <button className="btn btn-primary" data-testid="hr-record-payment" onClick={() => setModal({ type: "salary" })}>
              <Plus size={14} /> Record payment
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <StatCard label={`Payroll — ${s.month}`} value={fmt(s.month_total)} icon={BadgeDollarSign} testId="hr-stat-month" />
        <StatCard label="Payroll — this year" value={fmt(s.ytd_total)} icon={Wallet} testId="hr-stat-ytd" />
        <StatCard label="Outstanding advances" value={fmt(s.outstanding_advances)} icon={HandCoins} amber testId="hr-stat-advances" />
        <StatCard label="Pending approvals" value={s.pending_count} icon={CheckCircle2} testId="hr-stat-pending" />
      </div>

      {data.pending.length > 0 && (
        <div className="card p-4 mb-5 border-amber-200 bg-amber-50/40" data-testid="hr-pending-panel">
          <div className="font-bold text-sm text-amber-800 mb-2">Pending salary approvals — {s.month}</div>
          <div className="space-y-2">
            {data.pending.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center gap-3 text-sm bg-white rounded-lg border border-amber-100 px-3 py-2">
                <span className="font-medium">{p.employee_name}</span>
                <span className="muted text-xs">{p.month} · {p.method}</span>
                <span className="font-bold ml-auto">{fmt(p.amount, p.currency)}</span>
                <button className="btn btn-primary btn-sm" data-testid={`hr-approve-${p.id}`} disabled={busy === p.id}
                  onClick={() => approve(p)}>
                  {busy === p.id ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Approve & pay
                </button>
                {isAdmin && (
                  <button className="text-slate-400 hover:text-red-600" data-testid={`hr-del-pending-${p.id}`} onClick={() => removePayment(p)}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card overflow-hidden mb-5">
        <div className="px-4 pt-3 font-bold text-sm">Employees</div>
        <div className="overflow-x-auto">
          <table className="table w-full text-sm">
            <thead>
              <tr>
                <th>Employee</th><th>Role</th>
                <th style={{ textAlign: "right" }}>Monthly salary (UGX)</th>
                <th>Method</th>
                <th>NSSF/PAYE</th>
                <th style={{ textAlign: "right" }}>Advance owed</th>
                <th>Actions</th>
                {isAdmin && <th>HR access</th>}
              </tr>
            </thead>
            <tbody>
              {data.employees.map((e) => (
                <tr key={e.id}>
                  <td className="font-medium">
                    {e.full_name || e.email}
                    {e.standalone && <span className="ml-1.5 text-[10px] font-bold bg-slate-100 text-slate-500 rounded-full px-1.5 py-0.5">HR file</span>}
                  </td>
                  <td className="capitalize text-xs">{e.role}</td>
                  <td style={{ textAlign: "right" }}>
                    <input
                      type="number"
                      data-testid={`hr-salary-${e.id}`}
                      className="input !w-32 text-right inline-block"
                      defaultValue={e.monthly_salary || 0}
                      onBlur={(ev) => saveSalary(e, ev.target.value)}
                    />
                  </td>
                  <td className="text-xs">{e.payment_method}</td>
                  <td>
                    <button
                      data-testid={`hr-statutory-${e.id}`}
                      onClick={() => toggleStatutory(e)}
                      title="Auto-deduct NSSF (5%) & PAYE from salaries"
                      className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${e.statutory ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
                    >
                      {e.statutory ? "On ✓" : "Off"}
                    </button>
                  </td>
                  <td style={{ textAlign: "right" }} className={e.outstanding_advance > 0 ? "text-amber-700 font-semibold" : "muted"}>
                    {fmt(e.outstanding_advance)}
                  </td>
                  <td>
                    <button className="btn btn-ghost btn-sm" data-testid={`hr-pay-${e.id}`}
                      onClick={() => setModal({ employee: e, type: "salary" })}>
                      <BadgeDollarSign size={13} /> Pay salary
                    </button>
                    <button className="btn btn-ghost btn-sm" data-testid={`hr-advance-${e.id}`}
                      onClick={() => setModal({ employee: e, type: "advance" })}>
                      <HandCoins size={13} /> Advance
                    </button>
                    <button className="text-slate-500 hover:text-emerald-700 align-middle ml-1" title="Employee file"
                      data-testid={`hr-emp-edit-${e.id}`} onClick={() => setEmpModal({ ...EMPTY_EMP, ...e })}>
                      <Edit2 size={13} />
                    </button>
                    {isAdmin && e.standalone && (
                      <button className="text-slate-400 hover:text-red-600 align-middle ml-2" title="Delete employee file"
                        data-testid={`hr-emp-del-${e.id}`} onClick={() => removeEmployee(e)}>
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                  {isAdmin && (
                    <td>
                      {e.role !== "admin" && (
                        <button
                          data-testid={`hr-access-${e.id}`}
                          onClick={() => toggleHr(e)}
                          className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${e.hr_access ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
                        >
                          {e.hr_access ? "HR ✓" : "Grant"}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CashFloatSection employees={data.employees} isAdmin={isAdmin} />

      {empModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => !empSaving && setEmpModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="hr-emp-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <div className="font-bold text-sm">{empModal.id ? `Employee file — ${empModal.full_name || empModal.email}` : "Add employee"}</div>
              <button onClick={() => setEmpModal(null)}><X size={16} className="text-slate-400" /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3 overflow-y-auto flex-1 min-h-0">
              <label className="block col-span-2">
                <span className="text-xs muted">Full name *</span>
                <input data-testid="hr-emp-name" className="input w-full" value={empModal.full_name}
                  disabled={!!empModal.id && !empModal.standalone}
                  onChange={(e) => setEmpModal({ ...empModal, full_name: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-xs muted">Position / Job title</span>
                <input data-testid="hr-emp-position" className="input w-full" placeholder="e.g. Field Technician"
                  value={empModal.position} onChange={(e) => setEmpModal({ ...empModal, position: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-xs muted">Department</span>
                <input data-testid="hr-emp-department" className="input w-full" value={empModal.department}
                  onChange={(e) => setEmpModal({ ...empModal, department: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-xs muted">Phone</span>
                <input data-testid="hr-emp-phone" className="input w-full" value={empModal.phone}
                  onChange={(e) => setEmpModal({ ...empModal, phone: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-xs muted">Email</span>
                <input data-testid="hr-emp-email" className="input w-full" value={empModal.email}
                  disabled={!!empModal.id && !empModal.standalone}
                  onChange={(e) => setEmpModal({ ...empModal, email: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-xs muted">NIN (National ID)</span>
                <input data-testid="hr-emp-nin" className="input w-full" value={empModal.nin}
                  onChange={(e) => setEmpModal({ ...empModal, nin: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-xs muted">Date of birth</span>
                <input data-testid="hr-emp-dob" type="date" className="input w-full" value={empModal.dob}
                  onChange={(e) => setEmpModal({ ...empModal, dob: e.target.value })} />
              </label>
              <label className="block col-span-2">
                <span className="text-xs muted">Address</span>
                <input data-testid="hr-emp-address" className="input w-full" value={empModal.address}
                  onChange={(e) => setEmpModal({ ...empModal, address: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-xs muted">Start date</span>
                <input data-testid="hr-emp-start" type="date" className="input w-full" value={empModal.start_date}
                  onChange={(e) => setEmpModal({ ...empModal, start_date: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-xs muted">Emergency contact</span>
                <input data-testid="hr-emp-emergency" className="input w-full" placeholder="Name — phone"
                  value={empModal.emergency_contact} onChange={(e) => setEmpModal({ ...empModal, emergency_contact: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-xs muted">Monthly salary (UGX)</span>
                <input data-testid="hr-emp-salary" type="number" className="input w-full text-right"
                  value={empModal.monthly_salary} onChange={(e) => setEmpModal({ ...empModal, monthly_salary: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-xs muted">Payment method</span>
                <select data-testid="hr-emp-method" className="select w-full" value={empModal.payment_method}
                  onChange={(e) => setEmpModal({ ...empModal, payment_method: e.target.value })}>
                  <option>Bank</option><option>Mobile Money</option><option>Cash</option><option>Cheque</option>
                </select>
              </label>
              <label className="block col-span-2">
                <span className="text-xs muted">Account / Mobile money details</span>
                <input data-testid="hr-emp-account" className="input w-full" value={empModal.account_details}
                  onChange={(e) => setEmpModal({ ...empModal, account_details: e.target.value })} />
              </label>
              <label className="block col-span-2">
                <span className="text-xs muted">Notes</span>
                <input data-testid="hr-emp-notes" className="input w-full" value={empModal.notes}
                  onChange={(e) => setEmpModal({ ...empModal, notes: e.target.value })} />
              </label>
              {empModal.id && <EmployeeDocs userId={empModal.id} isAdmin={isAdmin} />}
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
              <button className="btn btn-ghost" onClick={() => setEmpModal(null)} data-testid="hr-emp-cancel">Cancel</button>
              <button className="btn btn-primary" onClick={saveEmployee} disabled={empSaving} data-testid="hr-emp-save">
                {empSaving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} {empModal.id ? "Save file" : "Add employee"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="px-4 pt-3 pb-2 flex items-center gap-3">
          <span className="font-bold text-sm">Payments ledger</span>
          <input
            type="month"
            data-testid="hr-month-filter"
            className="input !w-44 ml-auto"
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
          />
        </div>
        {payments.length === 0 ? (
          <Empty title="No payments recorded" subtitle="Record a salary, advance or reimbursement to get started" />
        ) : (
          <div className="overflow-x-auto">
            <table className="table w-full text-sm">
              <thead>
                <tr>
                  <th>Date</th><th>Employee</th><th>Type</th><th>Month</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                  <th style={{ textAlign: "right" }}>Deducted / Recovered</th>
                  <th>Method</th><th>Status</th><th>By</th><th></th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id}>
                    <td className="text-xs">{(p.paid_at || p.created_at || "").slice(0, 10)}</td>
                    <td className="font-medium">{p.employee_name}</td>
                    <td className="capitalize text-xs">{p.type}{p.auto ? " (auto)" : ""}</td>
                    <td className="text-xs">{p.month}</td>
                    <td style={{ textAlign: "right" }} className="font-semibold">{fmt(p.amount, p.currency)}</td>
                    <td style={{ textAlign: "right" }} className="text-xs text-amber-700">
                      {p.type === "advance"
                        ? `${fmt(p.recovered || 0)} recovered`
                        : (
                          <>
                            {(p.nssf_employee > 0 || p.paye > 0) && (
                              <div className="text-slate-500" data-testid={`hr-stat-ded-${p.id}`}>
                                NSSF -{fmt(p.nssf_employee)} · PAYE -{fmt(p.paye)}
                              </div>
                            )}
                            {p.advance_deducted > 0 ? `-${fmt(p.advance_deducted)} advance`
                              : (p.nssf_employee > 0 || p.paye > 0 ? "" : "—")}
                          </>
                        )}
                    </td>
                    <td className="text-xs">{p.method}</td>
                    <td>
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${p.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="text-xs muted">{p.created_by}</td>
                    <td className="whitespace-nowrap">
                      {p.type === "salary" && p.status === "paid" && (
                        <>
                          <button className="text-emerald-700 hover:text-emerald-900 mr-2" title="Download payslip PDF"
                            data-testid={`hr-payslip-${p.id}`} disabled={busy === `slip-${p.id}`}
                            onClick={() => downloadPayslip(p)}>
                            {busy === `slip-${p.id}` ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                          </button>
                          <button className="text-slate-500 hover:text-emerald-700 mr-2"
                            title={p.payslip_emailed_to ? `Payslip emailed to ${p.payslip_emailed_to}` : "Email payslip to employee"}
                            data-testid={`hr-email-payslip-${p.id}`} disabled={busy === `mail-${p.id}`}
                            onClick={() => emailPayslip(p)}>
                            {busy === `mail-${p.id}` ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} className={p.payslip_emailed_to ? "text-emerald-600" : ""} />}
                          </button>
                        </>
                      )}
                      {p.type === "advance" && p.status === "paid" && (p.amount - (p.recovered || 0)) > 0 && (
                        <button className="text-amber-600 hover:text-amber-800 mr-2" title="Mark recovered"
                          data-testid={`hr-recover-${p.id}`} onClick={() => recover(p)}>
                          <Undo2 size={14} />
                        </button>
                      )}
                      {isAdmin && (
                        <button className="text-slate-400 hover:text-red-600" data-testid={`hr-del-${p.id}`} onClick={() => removePayment(p)}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <PaymentModal
          employees={data.employees}
          initial={modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

const DOC_LABELS = ["Contract", "National ID", "Certificate", "CV / Resume", "Other"];

const EmployeeDocs = ({ userId, isAdmin }) => {
  const { toast } = useToast();
  const [docs, setDocs] = useState(null);
  const [label, setLabel] = useState("Contract");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.hrEmployeeDocs(userId).then(setDocs).catch(() => setDocs([]));
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      await api.hrUploadEmployeeDoc(userId, file, label);
      toast(`${label} attached`, "success");
      load();
    } catch (e) { toast(e?.response?.data?.detail || "Upload failed", "error"); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const download = async (d) => {
    try {
      const r = await api.hrDownloadEmployeeDoc(d.id);
      const url = URL.createObjectURL(new Blob([r.data], { type: d.mime_type }));
      const a = document.createElement("a");
      a.href = url; a.download = d.file_name; a.click();
      URL.revokeObjectURL(url);
    } catch { toast("Download failed", "error"); }
  };

  const del = async (d) => {
    if (!window.confirm(`Delete "${d.file_name}" (${d.label})?`)) return;
    try { await api.hrDeleteEmployeeDoc(d.id); toast("Document deleted", "success"); load(); }
    catch (e) { toast(e?.response?.data?.detail || "Failed", "error"); }
  };

  return (
    <div className="col-span-2 border-t border-slate-100 pt-3" data-testid="hr-emp-docs">
      <div className="text-xs font-bold uppercase tracking-wider muted mb-2">Documents — contracts, IDs, certificates</div>
      <div className="flex items-center gap-2 mb-2">
        <select data-testid="hr-doc-label" className="select !w-auto text-xs" value={label} onChange={(e) => setLabel(e.target.value)}>
          {DOC_LABELS.map((l) => <option key={l}>{l}</option>)}
        </select>
        <input ref={fileRef} type="file" data-testid="hr-doc-file" className="text-xs"
          accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" disabled={busy}
          onChange={(e) => upload(e.target.files?.[0])} />
        {busy && <Loader2 size={14} className="animate-spin text-emerald-700" />}
      </div>
      {docs === null ? (
        <div className="text-xs muted">Loading…</div>
      ) : docs.length === 0 ? (
        <div className="text-xs muted" data-testid="hr-docs-empty">No documents attached yet</div>
      ) : (
        <div className="space-y-1">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-2 text-xs bg-slate-50 rounded-lg px-2 py-1.5" data-testid={`hr-doc-${d.id}`}>
              <span className="font-bold text-[10px] bg-emerald-100 text-emerald-700 rounded-full px-1.5 py-0.5">{d.label}</span>
              <span className="font-medium truncate">{d.file_name}</span>
              <span className="muted ml-auto whitespace-nowrap">{(d.file_size / 1024).toFixed(0)} KB</span>
              <button className="text-slate-500 hover:text-emerald-700" title="Download"
                data-testid={`hr-doc-dl-${d.id}`} onClick={() => download(d)}>
                <FileText size={13} />
              </button>
              {isAdmin && (
                <button className="text-slate-400 hover:text-red-600" title="Delete"
                  data-testid={`hr-doc-del-${d.id}`} onClick={() => del(d)}>
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const EMPTY_EMP = {
  full_name: "", position: "", department: "", email: "", phone: "", nin: "",
  address: "", dob: "", start_date: "", emergency_contact: "", notes: "",
  monthly_salary: "", currency: "UGX", payment_method: "Bank", account_details: "",
};

const FLOAT_LABELS = { issue: "Cash issued", accounted: "Accounted (receipts)", return: "Cash returned" };

const CashFloatSection = ({ employees, isAdmin }) => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [showEntries, setShowEntries] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.hrFloats().then(setData).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.user_id || !(parseFloat(form.amount) > 0)) { toast("Pick a staff member and a valid amount", "error"); return; }
    setSaving(true);
    try {
      await api.hrAddFloat({ ...form, amount: parseFloat(form.amount) });
      toast(form.type === "accounted" ? "Recorded — expense posted to Books of Accounts" : "Float entry recorded", "success");
      setForm(null); load();
    } catch (e) { toast(e?.response?.data?.detail || "Failed", "error"); }
    setSaving(false);
  };

  const del = async (e) => {
    if (!window.confirm(`Delete this ${FLOAT_LABELS[e.type].toLowerCase()} entry of ${fmt(e.amount)}?${e.expense_id ? " Its expense entry will also be removed." : ""}`)) return;
    try { await api.hrDeleteFloat(e.id); toast("Entry deleted", "success"); load(); }
    catch (err) { toast(err?.response?.data?.detail || "Failed", "error"); }
  };

  const openForm = (type, userId = "") =>
    setForm({ user_id: userId, type, amount: "", description: "", date: new Date().toISOString().slice(0, 10) });

  return (
    <div className="card overflow-hidden mb-5" data-testid="hr-float-section">
      <div className="px-4 pt-3 pb-2 flex items-center gap-3 flex-wrap">
        <span className="font-bold text-sm flex items-center gap-2"><HandCoins size={15} /> Cash float tracking</span>
        <span className="text-xs muted">cash issued to staff vs what they accounted for</span>
        <button className="btn btn-secondary btn-sm ml-auto" data-testid="hr-float-new" onClick={() => openForm("issue")}>
          <Plus size={13} /> New float entry
        </button>
      </div>
      {!data || data.staff.length === 0 ? (
        <div className="px-4 pb-4 text-xs muted" data-testid="hr-float-empty">
          No float issued yet — use "New float entry" when you hand cash to a staff member for operations.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table w-full text-sm">
            <thead>
              <tr>
                <th>Staff</th>
                <th style={{ textAlign: "right" }}>Issued</th>
                <th style={{ textAlign: "right" }}>Accounted</th>
                <th style={{ textAlign: "right" }}>Returned</th>
                <th style={{ textAlign: "right" }}>Balance held</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.staff.map((s) => (
                <tr key={s.user_id} data-testid={`hr-float-row-${s.user_id}`}>
                  <td className="font-medium">{s.staff_name}</td>
                  <td style={{ textAlign: "right" }}>{fmt(s.issued)}</td>
                  <td style={{ textAlign: "right" }} className="text-emerald-700">{fmt(s.accounted)}</td>
                  <td style={{ textAlign: "right" }} className="muted">{fmt(s.returned)}</td>
                  <td style={{ textAlign: "right" }}
                    className={`font-extrabold ${s.balance > 0 ? "text-amber-700" : s.balance < 0 ? "text-red-600" : "text-emerald-700"}`}
                    data-testid={`hr-float-balance-${s.user_id}`}>
                    {fmt(s.balance)}
                  </td>
                  <td className="whitespace-nowrap text-xs">
                    <button className="text-slate-500 hover:text-emerald-700 mr-3" data-testid={`hr-float-issue-${s.user_id}`}
                      onClick={() => openForm("issue", s.user_id)}>+ Issue</button>
                    <button className="text-slate-500 hover:text-emerald-700 mr-3" data-testid={`hr-float-account-${s.user_id}`}
                      onClick={() => openForm("accounted", s.user_id)}>+ Account</button>
                    <button className="text-slate-500 hover:text-emerald-700" data-testid={`hr-float-return-${s.user_id}`}
                      onClick={() => openForm("return", s.user_id)}>+ Return</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.entries.length > 0 && (
        <div className="px-4 pb-3">
          <button className="text-xs font-semibold text-emerald-700" data-testid="hr-float-toggle-entries"
            onClick={() => setShowEntries(!showEntries)}>
            {showEntries ? "Hide" : "Show"} recent entries ({data.entries.length})
          </button>
          {showEntries && (
            <div className="mt-2 space-y-1">
              {data.entries.map((e) => (
                <div key={e.id} className="flex items-center gap-2 text-xs border-b border-slate-100 py-1" data-testid={`hr-float-entry-${e.id}`}>
                  <span className="muted w-20 shrink-0">{e.date}</span>
                  <span className="font-medium">{e.staff_name}</span>
                  <span className={`px-1.5 rounded-full font-bold text-[10px] ${e.type === "issue" ? "bg-amber-100 text-amber-700" : e.type === "accounted" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                    {FLOAT_LABELS[e.type]}
                  </span>
                  <span className="font-semibold">{fmt(e.amount)}</span>
                  <span className="muted truncate">{e.description}</span>
                  {isAdmin && (
                    <button className="ml-auto text-slate-400 hover:text-red-600" data-testid={`hr-float-del-${e.id}`} onClick={() => del(e)}>
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {form && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => !saving && setForm(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="hr-float-modal">
            <div className="font-bold text-sm mb-3 flex items-center gap-2"><HandCoins size={15} /> Cash float entry</div>
            <div className="space-y-3">
              <label className="block">
                <span className="text-xs muted">Staff member *</span>
                <select data-testid="hr-float-staff" className="select w-full" value={form.user_id}
                  onChange={(e) => setForm({ ...form, user_id: e.target.value })}>
                  <option value="">Select…</option>
                  {employees.map((e) => (<option key={e.id} value={e.id}>{e.full_name || e.email}</option>))}
                </select>
              </label>
              <label className="block">
                <span className="text-xs muted">Entry type</span>
                <select data-testid="hr-float-type" className="select w-full" value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  <option value="issue">Cash issued to staff</option>
                  <option value="accounted">Accounted for (receipts / spend) — posts to expenses</option>
                  <option value="return">Unused cash returned</option>
                </select>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs muted">Amount (UGX) *</span>
                  <input data-testid="hr-float-amount" type="number" min="0" className="input w-full text-right"
                    value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                </label>
                <label className="block">
                  <span className="text-xs muted">Date</span>
                  <input data-testid="hr-float-date" type="date" className="input w-full"
                    value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </label>
              </div>
              <label className="block">
                <span className="text-xs muted">Description</span>
                <input data-testid="hr-float-desc" className="input w-full" placeholder="e.g. Site survey transport & airtime"
                  value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </label>
              {form.type === "accounted" && (
                <div className="text-[11px] bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-emerald-800">
                  This amount will also be posted to <b>Books of Accounts</b> as an expense.
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button className="btn btn-ghost" onClick={() => setForm(null)} data-testid="hr-float-cancel">Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="hr-float-save">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Save entry
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MyPayslips = () => {
  const { toast } = useToast();
  const [slips, setSlips] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.myPayslips().then(setSlips).catch(() => setSlips([]));
  }, []);

  const download = async (p) => {
    setBusy(p.id);
    try {
      const r = await api.myPayslipPdf(p.id);
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `PAYSLIP_${p.month}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast("Failed to download payslip", "error"); }
    setBusy(null);
  };

  if (!slips) return <div className="p-8 text-center"><Loader2 className="animate-spin inline" /></div>;
  return (
    <div data-testid="page-my-payslips">
      <PageHeader icon={FileText} title="My Payslips"
        subtitle="Download your salary payslips — visible only to you" />
      {slips.length === 0 ? (
        <Empty title="No payslips yet" subtitle="Your payslips will appear here once a salary payment is processed" />
      ) : (
        <div className="grid gap-3 max-w-2xl">
          {slips.map((p) => {
            const ded = (p.nssf_employee || 0) + (p.paye || 0) + (p.advance_deducted || 0);
            const net = p.net_pay != null ? p.net_pay : Math.max((p.amount || 0) - ded, 0);
            return (
              <div key={p.id} className="card p-4 flex flex-wrap items-center gap-3" data-testid={`my-payslip-${p.id}`}>
                <div>
                  <div className="font-bold text-sm">{p.month}</div>
                  <div className="text-xs muted">Paid {(p.paid_at || "").slice(0, 10)} · {p.method}</div>
                </div>
                <div className="ml-auto text-right text-sm">
                  <div className="font-extrabold text-emerald-800">{fmt(net, p.currency)}</div>
                  <div className="text-[11px] muted">
                    Gross {fmt(p.amount, p.currency)}{ded > 0 ? ` · deductions -${fmt(ded, p.currency)}` : ""}
                  </div>
                </div>
                <button className="btn btn-primary btn-sm" data-testid={`my-payslip-dl-${p.id}`}
                  disabled={busy === p.id} onClick={() => download(p)}>
                  {busy === p.id ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />} Payslip PDF
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, amber, testId }) => (
  <div className={`card p-4 ${amber ? "border-amber-200" : ""}`} data-testid={testId}>
    <div className="flex items-center gap-2 text-xs muted mb-1"><Icon size={13} /> {label}</div>
    <div className={`text-lg font-extrabold ${amber ? "text-amber-700" : "text-slate-900"}`}>{value}</div>
  </div>
);

const PaymentModal = ({ employees, initial, onClose, onSaved }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    user_id: initial.employee?.id || "",
    type: initial.type || "salary",
    amount: initial.type === "salary" ? (initial.employee?.monthly_salary || "") : "",
    month: new Date().toISOString().slice(0, 7),
    method: initial.employee?.payment_method || "Bank",
    notes: "",
    deduct_advance: 0,
  });
  const [saving, setSaving] = useState(false);
  const emp = employees.find((e) => e.id === form.user_id);
  const outstanding = emp?.outstanding_advance || 0;

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!form.user_id) return toast("Pick an employee", "error");
    if (!(parseFloat(form.amount) > 0)) return toast("Enter a valid amount", "error");
    setSaving(true);
    try {
      await api.hrCreatePayment({
        ...form,
        amount: parseFloat(form.amount),
        deduct_advance: parseFloat(form.deduct_advance) || 0,
      });
      toast("Payment recorded & posted to Books of Accounts", "success");
      onSaved();
    } catch (e) {
      toast(e?.response?.data?.detail || "Failed to record payment", "error");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="hr-payment-modal">
        <div className="font-bold text-lg mb-4">Record payment</div>
        <div className="space-y-3 text-sm">
          <label className="block">
            <span className="text-xs muted">Employee</span>
            <select data-testid="hr-modal-employee" className="select w-full" value={form.user_id}
              onChange={(e) => {
                const em = employees.find((x) => x.id === e.target.value);
                upd({ user_id: e.target.value, method: em?.payment_method || "Bank",
                      amount: form.type === "salary" ? (em?.monthly_salary || "") : form.amount });
              }}>
              <option value="">— select —</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name || e.email}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs muted">Type</span>
              <select data-testid="hr-modal-type" className="select w-full" value={form.type}
                onChange={(e) => upd({ type: e.target.value, amount: e.target.value === "salary" ? (emp?.monthly_salary || "") : "" })}>
                <option value="salary">Salary</option>
                <option value="advance">Advance</option>
                <option value="bonus">Bonus</option>
                <option value="reimbursement">Reimbursement</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs muted">Amount (UGX)</span>
              <input data-testid="hr-modal-amount" type="number" className="input w-full text-right"
                value={form.amount} onChange={(e) => upd({ amount: e.target.value })} />
            </label>
            <label className="block">
              <span className="text-xs muted">Month</span>
              <input data-testid="hr-modal-month" type="month" className="input w-full" value={form.month}
                onChange={(e) => upd({ month: e.target.value })} />
            </label>
            <label className="block">
              <span className="text-xs muted">Method</span>
              <select data-testid="hr-modal-method" className="select w-full" value={form.method} onChange={(e) => upd({ method: e.target.value })}>
                <option>Bank</option><option>Mobile Money</option><option>Cash</option><option>Cheque</option>
              </select>
            </label>
          </div>
          {form.type === "salary" && outstanding > 0 && (
            <label className="block bg-amber-50 border border-amber-200 rounded-lg p-2.5">
              <span className="text-xs text-amber-800 font-semibold">
                Outstanding advance: {fmt(outstanding)} — deduct from this salary?
              </span>
              <input data-testid="hr-modal-deduct" type="number" className="input w-full text-right mt-1"
                value={form.deduct_advance} onChange={(e) => upd({ deduct_advance: e.target.value })} />
            </label>
          )}
          {form.type === "salary" && emp?.statutory && parseFloat(form.amount) > 0 && (() => {
            const { nssf, paye } = statutoryCalc(form.amount);
            const net = Math.max(parseFloat(form.amount) - nssf - paye - (parseFloat(form.deduct_advance) || 0), 0);
            return (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 text-xs" data-testid="hr-modal-statutory">
                <div className="font-semibold text-emerald-800 mb-1">Statutory deductions (auto)</div>
                <div className="flex justify-between"><span>NSSF employee (5%)</span><span>-{fmt(nssf)}</span></div>
                <div className="flex justify-between"><span>PAYE</span><span>-{fmt(paye)}</span></div>
                <div className="flex justify-between font-bold text-emerald-900 mt-1 pt-1 border-t border-emerald-200">
                  <span>Net pay</span><span data-testid="hr-modal-net">{fmt(net)}</span>
                </div>
              </div>
            );
          })()}
          <label className="block">
            <span className="text-xs muted">Notes</span>
            <input data-testid="hr-modal-notes" className="input w-full" value={form.notes}
              onChange={(e) => upd({ notes: e.target.value })} placeholder="Optional" />
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button className="btn btn-ghost" onClick={onClose} data-testid="hr-modal-cancel">Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="hr-modal-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Record & post expense
          </button>
        </div>
      </div>
    </div>
  );
};
